/* eslint formatjs/no-literal-string-in-jsx: "error" */

import React from "react"
import {
  useField,
  useFormContext,
  Text,
  Container,
  FlexContainer,
  Stack,
  FormInputProps
} from "@carrotfertility/carotene"
import {
  ButtonLabelInput,
  SelectButtonLabel,
  SelectButtonLabelWithInput,
  SelectButtonProps
} from "components/views/atoms/buttons/SelectButton"
import { FormattedMessage, useIntl } from "react-intl"

interface RadioButtonProps {
  value: string
  label: string
}
interface FormRadioButtonProps extends RadioButtonProps {
  submitOnClick?: boolean
  as?: React.FC<SelectButtonProps>
}

type FormRadioGroupProps = {
  name: string
  multiSelect?: boolean
  arrayForEmptyMultiSelect?: boolean
  useFieldOptions?: {
    defaultValue?: FormInputProps<string | number>["defaultValue"]
    validate?: FormInputProps<string>["validate"]
    defaultIsTouched?: FormInputProps<string>["defaultIsTouched"]
  }
}

const FormRadioGroupContext = React.createContext(null)
const useFormRadioGroup = () => React.useContext(FormRadioGroupContext)

export function FormRadioGroup({
  name,
  useFieldOptions = {},
  children,
  multiSelect = false,
  arrayForEmptyMultiSelect = false,
  ...otherProps
}: React.PropsWithChildren<FormRadioGroupProps>): JSX.Element {
  const { setValue, getInputProps, value, pushValue, removeValue, meta } = useField(name, useFieldOptions)

  React.useEffect(() => {
    if (!meta.isTouched) {
      const defaultValue = arrayForEmptyMultiSelect ? [] : ""
      setValue(() => useFieldOptions?.defaultValue ?? defaultValue, { isTouched: false })
    }
  }, [meta.isTouched, setValue, useFieldOptions?.defaultValue, arrayForEmptyMultiSelect])
  return (
    <FormRadioGroupContext.Provider value={{ multiSelect, name, value, pushValue, removeValue, ...getInputProps() }}>
      <fieldset {...otherProps}>{children}</fieldset>
    </FormRadioGroupContext.Provider>
  )
}

interface FormRadioButtonWithInputProps extends RadioButtonProps {
  inputName: string
  inputUseFieldOptions?: {
    defaultValue?: FormInputProps<string>["defaultValue"]
    validate?: FormInputProps<string>["validate"]
    defaultIsTouched?: FormInputProps<string>["defaultIsTouched"]
  }
  isInputRequired?: boolean
}

export function FormRadioButtonWithInput({
  value,
  label,
  inputName,
  inputUseFieldOptions,
  isInputRequired = false
}: FormRadioButtonWithInputProps): JSX.Element {
  const intl = useIntl()
  const context = useFormRadioGroup()
  const {
    name,
    value: groupValue,
    pushValue: _pushValue,
    removeValue: _removeValue,
    multiSelect: _multiSelect,
    ...rest
  } = context || {}

  const instance = useFormContext()
  const [showInput, setShowInput] = React.useState(false)
  const { setValue, getInputProps, value: inputValue, meta: inputMeta } = useField(inputName, inputUseFieldOptions)
  const isSelected = value === groupValue
  const ButtonLabel = React.useMemo(() => (isSelected && showInput ? SelectButtonLabelWithInput : SelectButtonLabel), [
    isSelected,
    showInput
  ])

  React.useEffect(() => {
    if (isSelected) {
      setShowInput(() => true)
    }
    if (!isSelected) {
      setValue(null)
    }
  }, [isSelected, setValue])

  React.useEffect(() => {
    if (!inputMeta.isTouched) {
      setValue(() => inputUseFieldOptions?.defaultValue ?? "", { isTouched: false })
    }
  }, [setValue, inputUseFieldOptions?.defaultValue, inputMeta.isTouched])

  async function onInputChange(event, onShowInput = false): Promise<void> {
    if (!onShowInput) {
      event.preventDefault()
    }
    instance.setMeta(() => ({ isTouched: true }))
    rest.onChange({ target: { value } })
    setShowInput(() => true)
  }

  return (
    <ButtonLabel
      onKeyDown={(event) => (event.keyCode === 32 || event.code === "Space") && onInputChange(event, true)}
      disabled={instance?.meta?.isSubmitting}
      outline={isSelected}
    >
      <input
        style={{ display: "none" }}
        disabled={instance?.meta?.isSubmitting}
        type="radio"
        name={name}
        value={value}
        checked={isSelected}
        {...rest}
        onChange={onInputChange}
      />
      {isSelected && showInput ? (
        <Stack spacing="tiny">
          <Container width="100%" paddingLeftRight="medium">
            <Text>{label}</Text>
          </Container>
          <ButtonLabelInput
            disabled={instance?.meta?.isSubmitting}
            required={isInputRequired}
            maxLength={200}
            placeholder={
              isInputRequired
                ? intl.formatMessage({ defaultMessage: "Required details" })
                : intl.formatMessage({ defaultMessage: "Optional details" })
            }
            name={inputName}
            {...getInputProps()}
          />
          <FlexContainer justifyContent="flex-end" paddingLeftRight="medium">
            {inputValue && inputValue.length >= 200 * 0.75 && (
              <span style={{ color: "#00000066" }}>
                <FormattedMessage
                  defaultMessage="{charactersRemaining, number} / {maximumCharactersAllowed, number}"
                  values={{
                    charactersRemaining: inputValue.length,
                    maximumCharactersAllowed: 200
                  }}
                />
              </span>
            )}
          </FlexContainer>
        </Stack>
      ) : (
        label
      )}
    </ButtonLabel>
  )
}

export type FormRadioButtonOption = {
  label: { defaultMessage: string }
  value: string
}

export function FormRadioButton({
  value,
  label,
  submitOnClick,
  as = SelectButtonLabel
}: FormRadioButtonProps): JSX.Element {
  const context = useFormRadioGroup()
  const instance = useFormContext()
  const SelectLabel = as
  const { name, multiSelect, value: groupValue, pushValue, removeValue, onChange, ...rest } = context || {}

  async function onInputChange(): Promise<void> {
    if (multiSelect) {
      if (Array.isArray(groupValue) && groupValue.indexOf(value) >= 0) {
        removeValue(groupValue.indexOf(value))
      } else {
        pushValue(value)
      }
    } else {
      await onChange({ target: { value } })
    }
    instance.setMeta(() => ({ isTouched: true }))
    if (typeof submitOnClick === "undefined" ? !multiSelect : submitOnClick) {
      instance.setMeta(() => ({ isSubmitting: true, isTouched: true }))
      // We are investigating a typing issue that only effects local carotene developement

      // @ts-ignore
      instance.onSubmit({ [name]: value })
    }
  }

  return (
    <SelectLabel
      disabled={instance?.meta?.isSubmitting}
      onKeyDown={(event) => (event.keyCode === 32 || event.code === "Space") && onInputChange()}
      outline={multiSelect ? Array.isArray(groupValue) && groupValue.includes(value) : value === groupValue}
    >
      {label}
      <input
        style={{ display: "none" }}
        disabled={instance?.meta?.isSubmitting}
        type={multiSelect ? "checkbox" : "radio"}
        name={name}
        value={value}
        onChange={onInputChange}
        checked={multiSelect ? Array.isArray(groupValue) && groupValue.includes(value) : value === groupValue}
        {...rest}
      />
    </SelectLabel>
  )
}
